import * as THREE from 'three';
import { MODELS, MODEL_PRICING, PROVIDER_COLORS_HEX } from '../data/models';
import { PROVIDERS } from '../data/providers';
import { state } from './state';
import { t, getCurrentLang } from './i18n';
import { escapeHtml, fmtUsd } from './ui-helpers';
import { getSpent } from './cost-tracking';
import { renderSelectedPills, updatePickerMeta } from './selected-pills';
import { renderCostLegend } from './cost-legend';

/* =================================================================
   6. Model Constellation — 3D picker
   ================================================================= */

/* Build a sprite-based text label that stays readable from any camera angle.
   The texture is rendered to a hi-res canvas (for crispness) and the sprite
   is scaled to a small world-space size so the label sits just below the
   sphere without overlapping it. */
function makeNodeLabel(text) {
  const canvas = document.createElement("canvas");
  const W = 512, H = 128;
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, W, H);

  ctx.font = "700 56px 'Inter', -apple-system, BlinkMacSystemFont, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  // Dark stroke for legibility against the matrix rain
  ctx.strokeStyle = "rgba(7, 11, 20, 0.95)";
  ctx.lineWidth = 10;
  ctx.lineJoin = "round";
  ctx.miterLimit = 2;
  ctx.strokeText(text, W / 2, H / 2);

  // White fill — sprite material's color tints this for state changes
  ctx.fillStyle = "#FFFFFF";
  ctx.fillText(text, W / 2, H / 2);

  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;

  const mat = new THREE.SpriteMaterial({
    map: tex,
    transparent: true,
    depthWrite: false,
    color: 0xCFCFCF,
  });
  const sprite = new THREE.Sprite(mat);
  // World-space size: width ~1.6 units, height ~0.4 (ratio matches canvas 4:1)
  sprite.scale.set(1.6, 0.4, 1);
  return sprite;
}

export class ModelConstellation {
  constructor(canvas, models, providers, state, callbacks) {
    this.canvas = canvas;
    this.models = models;
    this.providers = providers;
    this.state = state;
    this.callbacks = callbacks; // { onToggle, onHover }
    this.runningIds = new Set();
    this.synthMode = false;
    this.synthStart = 0;
    this.beams = []; // active beams during run
    this.init();
    this.attachEvents();
    this.updateSelected();
    this.animate();
  }

  init() {
    const w = this.canvas.clientWidth || 800;
    const h = this.canvas.clientHeight || 480;

    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.FogExp2(0x070b14, 0.035);

    this.camera = new THREE.PerspectiveCamera(48, w / h, 0.1, 100);
    this.camera.position.set(0, 1.6, 9);
    this.camera.lookAt(0, 0, 0);

    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas, alpha: true, antialias: true,
    });
    this.renderer.setSize(w, h, false);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    // Lighting
    this.scene.add(new THREE.AmbientLight(0x223344, 0.45));
    const pl = new THREE.PointLight(0x00FF9C, 2.2, 24);
    pl.position.set(0, 0, 0);
    this.scene.add(pl);
    const dir = new THREE.DirectionalLight(0x7FFFD4, 0.45);
    dir.position.set(4, 6, 4);
    this.scene.add(dir);

    // Center pupil (the iris)
    this.pupil = new THREE.Mesh(
      new THREE.SphereGeometry(0.6, 48, 48),
      new THREE.MeshStandardMaterial({
        color: 0x00FF9C,
        emissive: 0x00FF9C,
        emissiveIntensity: 1.5,
        roughness: 0.25,
        metalness: 0.4,
      })
    );
    this.scene.add(this.pupil);

    // Outer glow shell
    this.pupilGlow = new THREE.Mesh(
      new THREE.SphereGeometry(0.85, 32, 32),
      new THREE.MeshBasicMaterial({
        color: 0x00FF9C, transparent: true, opacity: 0.12,
        side: THREE.BackSide,
      })
    );
    this.scene.add(this.pupilGlow);

    // Concentric iris rings
    this.rings = [];
    const ringSpecs = [
      { r: 1.5, opacity: 0.42, thickness: 0.014 },
      { r: 2.0, opacity: 0.28, thickness: 0.010 },
      { r: 2.6, opacity: 0.18, thickness: 0.008 },
    ];
    ringSpecs.forEach((spec, i) => {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(spec.r, spec.thickness, 10, 128),
        new THREE.MeshBasicMaterial({
          color: 0x00FF9C, transparent: true, opacity: spec.opacity,
        })
      );
      ring.rotation.x = (i + 1) * 0.4;
      ring.rotation.y = (i + 1) * 0.3;
      ring.userData.tiltX = (Math.random() - 0.5) * 0.0015;
      ring.userData.tiltY = (Math.random() - 0.5) * 0.0025;
      this.scene.add(ring);
      this.rings.push(ring);
    });

    // Background dust particles (subtle depth)
    const dustGeom = new THREE.BufferGeometry();
    const dustCount = 200;
    const dustPos = new Float32Array(dustCount * 3);
    for (let i = 0; i < dustCount; i++) {
      const r = 5 + Math.random() * 6;
      const theta = Math.random() * Math.PI * 2;
      const phi = (Math.random() - 0.5) * Math.PI;
      dustPos[i * 3]     = r * Math.cos(theta) * Math.cos(phi);
      dustPos[i * 3 + 1] = r * Math.sin(phi);
      dustPos[i * 3 + 2] = r * Math.sin(theta) * Math.cos(phi);
    }
    dustGeom.setAttribute("position", new THREE.BufferAttribute(dustPos, 3));
    this.dust = new THREE.Points(
      dustGeom,
      new THREE.PointsMaterial({
        color: 0x00FF9C, size: 0.04, transparent: true, opacity: 0.35,
      })
    );
    this.scene.add(this.dust);

    // Model nodes — distributed on a tilted ring (sphere + always-visible label)
    const N = this.models.length;
    this.nodes = this.models.map((m, i) => {
      const angle = (i / N) * Math.PI * 2;
      const orbitR = 4.4;
      const colorHex = PROVIDER_COLORS_HEX[m.provider] || 0xffffff;
      const node = new THREE.Mesh(
        new THREE.SphereGeometry(0.20, 28, 28),
        new THREE.MeshStandardMaterial({
          color: colorHex,
          emissive: colorHex,
          emissiveIntensity: 0.6,
          roughness: 0.35,
          metalness: 0.2,
        })
      );

      // Permanent floating label below the sphere
      const label = makeNodeLabel(m.label);
      label.userData.targetTint = new THREE.Color(0xCFCFCF);
      this.scene.add(label);

      node.userData = {
        model: m,
        angle,
        speed: 0.10 + Math.random() * 0.12,
        orbitR,
        targetOrbitR: orbitR,
        targetEmissive: 0.6,
        targetScale: 1.0,
        tilt: (Math.random() - 0.5) * 1.6,
        tiltSpeed: 0.6 + Math.random() * 0.6,
        flash: 0,
        flashColor: 0x00FF9C,
        label,
      };
      this.scene.add(node);
      return node;
    });

    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2(-10, -10);
    this.hoveredNode = null;
  }

  attachEvents() {
    this.canvas.addEventListener("pointermove", e => {
      const rect = this.canvas.getBoundingClientRect();
      this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      this.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      this.lastMouseClient = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    });
    this.canvas.addEventListener("pointerleave", () => {
      this.mouse.set(-10, -10);
      this.hoveredNode = null;
      this.callbacks.onHover(null);
      this.canvas.style.cursor = "default";
    });
    this.canvas.addEventListener("click", () => {
      if (this.hoveredNode) {
        this.callbacks.onToggle(this.hoveredNode.userData.model.id);
        this.updateSelected();
      }
    });
    window.addEventListener("resize", () => this.onResize());
    // ResizeObserver fires when panel layout changes (lang switch, etc.)
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(() => this.onResize());
      ro.observe(this.canvas);
    }
  }

  onResize() {
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    if (w === 0 || h === 0) return;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h, false);
  }

  updateSelected() {
    this.nodes.forEach(node => {
      const sel = this.state.selectedModels.has(node.userData.model.id);
      node.userData.targetOrbitR = sel ? 2.7 : 4.4;
      node.userData.targetEmissive = sel ? 1.4 : 0.55;
      node.userData.targetScale = sel ? 1.35 : 1.0;
      // Label tint: matrix green when selected, white-ish when not
      if (node.userData.label) {
        node.userData.label.userData.targetTint = new THREE.Color(sel ? 0x00FF9C : 0xCFCFCF);
        node.userData.label.userData.targetOpacity = sel ? 1.0 : 0.78;
      }
    });
  }

  setRunning(modelIds) {
    this.runningIds = new Set(modelIds);
    this.clearBeams();
    if (this.runningIds.size === 0) return;
    // Create beams from center to each running node
    this.nodes.forEach(node => {
      if (this.runningIds.has(node.userData.model.id)) {
        const colorHex = PROVIDER_COLORS_HEX[node.userData.model.provider] || 0x00FF9C;
        const geom = new THREE.BufferGeometry().setFromPoints([
          new THREE.Vector3(0, 0, 0),
          node.position.clone(),
        ]);
        const mat = new THREE.LineBasicMaterial({
          color: 0x00FF9C, transparent: true, opacity: 0.55,
        });
        const line = new THREE.Line(geom, mat);
        line.userData.targetNode = node;
        line.userData.colorHex = colorHex;
        this.scene.add(line);
        this.beams.push(line);
      }
    });
  }

  clearBeams() {
    this.beams.forEach(l => {
      this.scene.remove(l);
      l.geometry.dispose();
      l.material.dispose();
    });
    this.beams = [];
  }

  flashNode(modelId, ok) {
    const node = this.nodes.find(n => n.userData.model.id === modelId);
    if (!node) return;
    node.userData.flash = 1.0;
    node.userData.flashColor = ok ? 0x00FF9C : 0xF38281;
    this.runningIds.delete(modelId);
    // Remove the beam for this model
    const beamIdx = this.beams.findIndex(l => l.userData.targetNode === node);
    if (beamIdx >= 0) {
      const beam = this.beams[beamIdx];
      this.scene.remove(beam);
      beam.geometry.dispose();
      beam.material.dispose();
      this.beams.splice(beamIdx, 1);
    }
  }

  triggerSynthesize(synthModelId) {
    this.synthMode = true;
    this.synthStart = performance.now();
    this.synthTargetId = synthModelId;
    setTimeout(() => { this.synthMode = false; }, 2800);
  }

  animate() {
    const now = performance.now() * 0.001;

    // Pupil pulse
    const basePulse = 1 + Math.sin(now * 1.3) * 0.05;
    this.pupil.scale.setScalar(basePulse);
    this.pupilGlow.scale.setScalar(basePulse * 1.05);

    // Synth burst override
    let pupilEmissive = 1.5;
    if (this.synthMode) {
      const elapsed = (performance.now() - this.synthStart) / 2800;
      const t = Math.min(elapsed, 1);
      const burst = 1 + Math.sin(t * Math.PI) * 1.6;
      this.pupil.scale.setScalar(basePulse * burst);
      this.pupilGlow.scale.setScalar(basePulse * burst * 1.15);
      pupilEmissive = 1.5 + Math.sin(t * Math.PI) * 2.2;
    }
    this.pupil.material.emissiveIntensity +=
      (pupilEmissive - this.pupil.material.emissiveIntensity) * 0.12;

    // Rings rotate
    this.rings.forEach((ring) => {
      ring.rotation.x += ring.userData.tiltX + 0.0015;
      ring.rotation.y += ring.userData.tiltY + 0.0020;
    });

    // Dust slow rotation
    if (this.dust) {
      this.dust.rotation.y += 0.0006;
      this.dust.rotation.x += 0.0002;
    }

    // Camera drift (no manual orbit controls needed)
    this.camera.position.x = Math.sin(now * 0.06) * 1.0;
    this.camera.position.y = 1.6 + Math.cos(now * 0.045) * 0.35;
    this.camera.position.z = 9 + Math.sin(now * 0.035) * 0.5;
    this.camera.lookAt(0, 0, 0);

    // Update nodes
    this.nodes.forEach(node => {
      const ud = node.userData;
      ud.angle += ud.speed * 0.005;

      // Smooth orbit interpolation
      ud.orbitR += (ud.targetOrbitR - ud.orbitR) * 0.05;

      const x = Math.cos(ud.angle) * ud.orbitR;
      const z = Math.sin(ud.angle) * ud.orbitR;
      const y = Math.sin(ud.angle * ud.tiltSpeed + ud.tilt) * 0.7;

      // Synth: collapse selected nodes toward center
      if (this.synthMode && this.state.selectedModels.has(ud.model.id)) {
        const elapsed = (performance.now() - this.synthStart) / 2800;
        const t = Math.min(elapsed, 1);
        // Ease-in collapse, then expand back
        const collapse = t < 0.6
          ? 1 - (t / 0.6) * 0.85   // collapse to 15%
          : 0.15 + ((t - 0.6) / 0.4) * 0.85; // expand back
        node.position.set(x * collapse, y * collapse, z * collapse);
      } else {
        node.position.set(x, y, z);
      }

      // Emissive flash
      let targetE = ud.targetEmissive;
      if (ud.flash > 0.01) {
        targetE = 0.8 + ud.flash * 2.4;
        node.material.color.setHex(ud.flashColor);
        node.material.emissive.setHex(ud.flashColor);
        ud.flash *= 0.93;
      } else if (ud.flash !== 0) {
        ud.flash = 0;
        const baseColor = PROVIDER_COLORS_HEX[ud.model.provider] || 0xffffff;
        node.material.color.setHex(baseColor);
        node.material.emissive.setHex(baseColor);
      }

      // Pulsing emissive for running models
      if (this.runningIds.has(ud.model.id)) {
        targetE = 1.0 + Math.sin(now * 4) * 0.6;
      }

      node.material.emissiveIntensity +=
        (targetE - node.material.emissiveIntensity) * 0.12;

      // Scale interpolation (with running pulse)
      let ts = ud.targetScale;
      if (this.runningIds.has(ud.model.id)) {
        ts = ud.targetScale * (1 + Math.sin(now * 4) * 0.08);
      }
      const cs = node.scale.x;
      node.scale.setScalar(cs + (ts - cs) * 0.12);

      // Position the label just below the sphere (account for current scale)
      if (ud.label) {
        const off = 0.40 + node.scale.x * 0.10;
        ud.label.position.set(node.position.x, node.position.y - off, node.position.z);

        // Smooth tint and opacity interpolation
        const targetTint = ud.label.userData.targetTint || new THREE.Color(0xCFCFCF);
        ud.label.material.color.lerp(targetTint, 0.10);
        const targetOp = ud.label.userData.targetOpacity != null ? ud.label.userData.targetOpacity : 0.78;
        ud.label.material.opacity += (targetOp - ud.label.material.opacity) * 0.10;

        // Hovered label gets a small boost for legibility
        if (this.hoveredNode === node) {
          ud.label.material.opacity = Math.max(ud.label.material.opacity, 1.0);
        }
      }
    });

    // Update beams
    this.beams.forEach(line => {
      const node = line.userData.targetNode;
      const positions = line.geometry.attributes.position.array;
      positions[3] = node.position.x;
      positions[4] = node.position.y;
      positions[5] = node.position.z;
      line.geometry.attributes.position.needsUpdate = true;
      // Pulsing opacity
      line.material.opacity = 0.35 + Math.sin(now * 4) * 0.30;
    });

    // Raycasting (skip during synth burst — locked animation)
    if (!this.synthMode) {
      this.raycaster.setFromCamera(this.mouse, this.camera);
      const hits = this.raycaster.intersectObjects(this.nodes);
      if (hits.length > 0) {
        const newHit = hits[0].object;
        if (this.hoveredNode !== newHit) {
          this.hoveredNode = newHit;
          this.canvas.style.cursor = "pointer";
          this.callbacks.onHover(this.hoveredNode.userData.model, this.lastMouseClient);
        } else {
          // Update tooltip position on every move while hovering same node
          this.callbacks.onHover(this.hoveredNode.userData.model, this.lastMouseClient);
        }
      } else if (this.hoveredNode) {
        this.hoveredNode = null;
        this.canvas.style.cursor = "default";
        this.callbacks.onHover(null);
      }
    }

    this.renderer.render(this.scene, this.camera);
    requestAnimationFrame(() => this.animate());
  }
}

export let constellation = null;
let tooltipEl = null;

export function getConstellation() { return constellation; }

export function onConstellationToggle(modelId) {
  const wasSelected = state.selectedModels.has(modelId);
  if (wasSelected) state.selectedModels.delete(modelId);
  else state.selectedModels.add(modelId);
  localStorage.setItem("lp_aiboard_selected", JSON.stringify([...state.selectedModels]));
  updatePickerMeta();
  renderSelectedPills();
  renderCostLegend();
  // Tell the runner to update the run-button + add/remove pending cards.
  window.dispatchEvent(new CustomEvent("lp-constellation-toggle", {
    detail: { modelId, wasSelected }
  }));
}

export function onConstellationHover(model, mouseClient) {
  if (!tooltipEl) tooltipEl = document.getElementById("constellation-tooltip");
  if (!model) {
    tooltipEl.classList.remove("show");
    return;
  }
  const provider = PROVIDERS[model.provider];
  const isSelected = state.selectedModels.has(model.id);
  const sp = getSpent(model.id);
  const p = MODEL_PRICING[model.id];
  const priceLine = p
    ? `<div class="tt-price">${fmtUsd(p.in)}/M in · ${fmtUsd(p.out)}/M out</div>`
    : "";
  const spentLine = sp > 0
    ? `<div class="tt-spent">${t("cost.session")} <strong>${fmtUsd(sp)}</strong></div>`
    : "";
  tooltipEl.innerHTML = `
    <div class="tt-name">${escapeHtml(model.label)}</div>
    <div class="tt-meta">${escapeHtml(provider.label)} · ${escapeHtml(model.family)}</div>
    ${priceLine}
    ${spentLine}
    <div class="tt-action">${isSelected ? t("cost.deselect") : t("cost.select")}</div>
  `;
  tooltipEl.style.left = mouseClient.x + "px";
  tooltipEl.style.top = mouseClient.y + "px";
  tooltipEl.classList.add("show");
}

export function initConstellation() {
  const canvas = document.getElementById("constellation");
  if (!canvas || typeof THREE === "undefined") return;
  // Skip deprecated entries in the 3D picker for the same reason the
  // synth-model dropdown does: they duplicate the visible node cloud with
  // stale aliases that resolve to the same wire call as their successor.
  // MODELS keeps the legacy rows for label / pricing on saved sessions.
  const visibleModels = MODELS.filter(m => !m.deprecated);
  // Ensure the canvas has measurable size before init
  requestAnimationFrame(() => {
    constellation = new ModelConstellation(canvas, visibleModels, PROVIDERS, state, {
      onToggle: onConstellationToggle,
      onHover: onConstellationHover,
    });
  });

  // Hook the spent-update event so the tooltip refreshes if hovering same model
  window.addEventListener("lp-spent-updated", (ev) => {
    if (constellation && constellation.hoveredNode &&
        constellation.hoveredNode.userData.model.id === ev.detail.modelId) {
      onConstellationHover(constellation.hoveredNode.userData.model, constellation.lastMouseClient);
    }
    renderCostLegend();
  });

  // Hook the toggle event from the cost-legend chips
  window.addEventListener("lp-toggle-model", (ev) => {
    onConstellationToggle(ev.detail.modelId);
    if (constellation) constellation.updateSelected();
  });

  // Hook the selection-changed event from selected-pills (× clicks)
  window.addEventListener("lp-selection-changed", (_ev) => {
    updatePickerMeta();
    renderSelectedPills();
    if (constellation) constellation.updateSelected();
  });
}
